// Runtime context for the ported event dashboard inside the teliance-events
// SPA. The dashboard was single-tenant (hardcoded HOST_ID, relative /api/*
// URLs). Here every request is per-tenant: the wrapper view calls
// setDashboardTenant(slug) on mount, and the data layer (api.js, the ws hooks,
// pushNotifications.js) builds tenant-scoped URLs + carries the SPA auth token.

import { refreshAccessToken } from '@/lib/api.js';

let _slug = null;
let _venueTz = 'America/New_York';

export function setDashboardTenant(slug) { _slug = slug; }
export function getTenantSlug() { return _slug; }

// Venue timezone for this tenant (set from the config GET on mount). All
// dashboard date/time helpers default to it so cards/timestamps show venue-local
// (e.g. EDT), never the browser's zone.
export function setVenueTz(tz) { _venueTz = tz || 'America/New_York'; }
export function getVenueTz() { return _venueTz; }

// If the stored token is expired or within `skewMs` of expiring, refresh it so a
// ws (re)connect carries a valid token — the server validates the token only at
// the upgrade, so this lets real-time survive a token refresh WITHOUT depending
// on a REST call having refreshed first. Best-effort: a failed/indeterminate
// refresh just leaves the existing token (the upgrade may then 401 and retry,
// same as before). No-op (no network) when the token is comfortably valid.
export async function ensureFreshToken(skewMs = 120000) {
  const token = getToken();
  if (!token) return;
  let exp = null;
  try { exp = JSON.parse(atob(token.split('.')[1])).exp * 1000; } catch { return; }
  if (exp && exp - Date.now() > skewMs) return; // still valid with margin
  try { await refreshAccessToken(); } catch { /* leave existing token */ }
}

// REST base: /api/tenants/<slug>/event-dashboard
export function getApiBase() {
  return `/api/tenants/${_slug}/event-dashboard`;
}

export function getToken() {
  try { return localStorage.getItem('token'); } catch { return null; }
}

// ws(s)://<host>/ws?tenant=<slug>[&token=<jwt>]
// Browser WebSocket can't set an Authorization header, so the token rides as a
// query param (the upgrade handler reads it when DEV_NO_AUTH is off).
export function getWsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const q = new URLSearchParams({ tenant: _slug || '' });
  const token = getToken();
  if (token) q.set('token', token);
  return `${proto}//${window.location.host}/ws?${q.toString()}`;
}
